import React, { useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AsyncPaginate } from "react-select-async-paginate";
import { Link } from "react-router-dom";
import {
  BsBoxSeam,
  BsArrowDownLeft,
  BsArrowUpRight,
  BsCalculator,
  BsCalendar,
  BsBuilding,
  BsPrinter,
} from "react-icons/bs";
import { authFetch } from "../utils/authFetch";

const LIMIT = 20;

// Helper to format Date objects to YYYY-MM-DD
const formatDateString = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Generic paged loader for React Select Async Paginate
const loadPaged = async (url, q, page, limit) => {
  const skip = (page - 1) * limit;
  const sep = url.includes("?") ? "&" : "?";
  const full = `${url}${sep}search=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`;
  const data = await authFetch(full);

  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const total =
    typeof data?.total === "number"
      ? data.total
      : typeof data?.count === "number"
        ? data.count
        : undefined;

  const hasMore =
    typeof total === "number"
      ? skip + items.length < total
      : items.length === limit;
  return { items, hasMore };
};

// Map backend doc_type to human readable Romanian labels
const getDocTypeLabel = (docType) => {
  if (docType === undefined || docType === null) return "—";
  const rt = Number(docType);
  if (isNaN(rt)) {
    const s = String(docType).toLowerCase();
    if (s.includes("nir")) return "NIR";
    if (s.includes("intrare")) return "Factură Intrare";
    if (s.includes("bon_consum") || s.includes("consum")) return "Bon Consum";
    if (s.includes("transfer")) return "Bon Transfer";
    if (s.includes("iesire")) return "Factură Ieșire";
    if (s.includes("proces_verbal") || s.includes("proces")) return "Proces Verbal";
    return String(docType).toUpperCase();
  }
  switch (rt) {
    case 0: return "Factură Intrare";
    case 1: return "NIR";
    case 2: return "Proces Verbal";
    case 3: return "Bon Consum";
    case 4: return "Bon Transfer";
    case 5: return "Ieșire (Factură/Bon)";
    default: return `Document (${rt})`;
  }
};

// Get the corresponding routing path for document links
const getDocLink = (docType, docId) => {
  if (!docId) return null;
  const rt = Number(docType);
  switch (rt) {
    case 0: return `/intrare/${docId}/edit`;
    case 1: return `/nir/${docId}/edit`;
    case 2: return `/proces-verbal/${docId}/edit`;
    case 3: return `/bon-consum/${docId}`;
    case 4: return `/bon-transfer/${docId}`;
    case 5: return `/iesire/${docId}`;
    default: return null;
  }
};

export default function FisaMagazie() {
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);
  const [gestiune, setGestiune] = useState(null);
  const [product, setProduct] = useState(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const loadGestiuneOptions = async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const qq = (inputValue || "").trim();
    try {
      const { items: rows, hasMore } = await loadPaged("/bxgestiune/", qq, page, LIMIT);
      const options = rows.map((g) => ({
        value: g.id,
        label: `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
      }));
      return { options, hasMore, additional: { page: page + 1 } };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const loadProductOptions = async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const qq = (inputValue || "").trim();
    try {
      const { items: rows, hasMore } = await loadPaged("/bxpart/", qq, page, LIMIT);
      const options = rows.map((p) => ({
        value: p.id,
        label: `${p.cod ?? p.id} — ${p.denumire ?? ""}`.trim(),
      }));
      return { options, hasMore, additional: { page: page + 1 } };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const loadData = async () => {
    if (!product) {
      setErr("Te rugăm să selectezi un produs.");
      return;
    }
    setBusy(true);
    setErr("");
    setData(null);
    setLoaded(false);

    try {
      const usp = new URLSearchParams();
      usp.set("bxpart_id", String(product.value));
      if (gestiune) usp.set("bxgestiune_id", String(gestiune.value));
      if (from) usp.set("date_from", formatDateString(from));
      if (to) usp.set("date_to", formatDateString(to));

      const res = await authFetch(`/stock/ledger/movements?${usp.toString()}`);
      if (res && typeof res === "object") {
        setData(res);
        setLoaded(true);
      } else {
        setErr("Format răspuns invalid de la server.");
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcarea fișei de magazie.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Helper formats
  const fmtQty = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const fmtPrice = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  const fmtVal = (n) => {
    const val = Number(n ?? 0);
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateRo = (val) => {
    if (!val) return "";
    const s = String(val);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("ro-RO");
  };

  // Calculate IN / OUT totals for the loaded period
  const totals = useMemo(() => {
    if (!data?.movements) return { inQty: 0, outQty: 0, inVal: 0, outVal: 0 };
    let inQty = 0;
    let outQty = 0;
    let inVal = 0;
    let outVal = 0;

    data.movements.forEach((m) => {
      const q = Number(m.qty || 0);
      const p = Number(m.price || 0);
      if (m.direction === "IN") {
        inQty += q;
        inVal += q * p;
      } else {
        outQty += q;
        outVal += q * p;
      }
    });

    return { inQty, outQty, inVal, outVal };
  }, [data]);

  return (
    <div className="p-3">
      {/* Header card with gradient background */}
      <Card className="shadow-sm border-0 rounded-4 p-4 mb-4 bg-white d-print-none">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <BsBoxSeam className="text-primary" /> Fișă de Magazie / Mișcări
            </h4>
            <p className="text-muted small mb-0">
              Vizualizează istoricul cronologic al mișcărilor (intrări, ieșiri, transferuri) pentru produsul selectat.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handlePrint}
              disabled={!loaded}
              className="rounded-3 fw-semibold d-flex align-items-center gap-1"
            >
              <BsPrinter /> Printează
            </Button>
            <Button
              variant="primary"
              onClick={loadData}
              disabled={busy || !product}
              className="rounded-3 fw-semibold"
            >
              {busy ? "Se încarcă..." : "Generează Raport"}
            </Button>
          </div>
        </div>

        {/* Filter Form */}
        <Form className="bg-light p-3 rounded-4 border border-light-subtle">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1 d-flex align-items-center gap-1">
                <BsBuilding /> Gestiune (Opțional)
              </Form.Label>
              <AsyncPaginate
                value={gestiune}
                loadOptions={loadGestiuneOptions}
                onChange={setGestiune}
                isClearable
                placeholder="Toate gestiunile..."
                className="shadow-sm border-0"
                debounceTimeout={300}
                cacheOptions
                defaultOptions
                openMenuOnFocus
                additional={{ page: 1 }}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold text-secondary small mb-1 d-flex align-items-center gap-1">
                <BsBoxSeam /> Produs / Articol <span className="text-danger">*</span>
              </Form.Label>
              <AsyncPaginate
                value={product}
                loadOptions={loadProductOptions}
                onChange={setProduct}
                placeholder="Caută și alege produs..."
                className="shadow-sm border-0"
                debounceTimeout={300}
                cacheOptions
                defaultOptions
                openMenuOnFocus
                additional={{ page: 1 }}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={12} md={5}>
              <Form.Label className="fw-semibold text-secondary small mb-1 d-flex align-items-center gap-1">
                <BsCalendar /> Perioadă
              </Form.Label>
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  selected={from}
                  onChange={(date) => setFrom(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="De la..."
                  className="form-control rounded-3 border-0 shadow-sm"
                />
                <span className="text-secondary">—</span>
                <DatePicker
                  selected={to}
                  onChange={(date) => setTo(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Până la..."
                  className="form-control rounded-3 border-0 shadow-sm"
                />
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      {err && (
        <Alert variant="danger" className="rounded-3 mb-4 d-print-none">
          {err}
        </Alert>
      )}

      {/* Print View Header (only visible on print) */}
      <div className="d-none d-print-block mb-4">
        <h3 className="fw-bold text-center mb-1">FIȘĂ DE MAGAZIE</h3>
        <p className="text-center text-muted small">
          Perioada: {from ? formatDateRo(from) : "—"} — {to ? formatDateRo(to) : "—"}
        </p>
        <hr />
        <Row className="mb-3">
          <Col xs={6}>
            <strong>Produs:</strong> {product?.label || "—"}
          </Col>
          <Col xs={6}>
            <strong>Gestiune:</strong> {gestiune?.label || "Toate gestiunile"}
          </Col>
        </Row>
      </div>

      {loaded && data && (
        <>
          {/* KPI Metrics Cards */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-secondary small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsCalculator /> Stoc Inițial
                </div>
                <h3 className="fw-bold mb-0 text-dark font-monospace">
                  {fmtQty(data.initial_stock)}
                </h3>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-success small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsArrowDownLeft /> Total Intrări
                </div>
                <h3 className="fw-bold mb-0 text-success font-monospace">
                  {fmtQty(totals.inQty)}
                </h3>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100 text-center text-md-start">
                <div className="text-danger small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start">
                  <BsArrowUpRight /> Total Ieșiri
                </div>
                <h3 className="fw-bold mb-0 text-danger font-monospace">
                  {fmtQty(totals.outQty)}
                </h3>
              </Card>
            </Col>

            <Col xs={6} md={3}>
              <Card
                className={`border-0 shadow-sm rounded-4 p-3 h-100 text-center text-md-start text-white ${
                  data.final_stock > 0 ? "bg-success" : "bg-secondary"
                }`}
              >
                <div className="small fw-semibold mb-1 d-flex align-items-center gap-1 justify-content-center justify-content-md-start opacity-75">
                  <BsBoxSeam /> Stoc Final
                </div>
                <h3 className="fw-bold mb-0 font-monospace">
                  {fmtQty(data.final_stock)}
                </h3>
              </Card>
            </Col>
          </Row>

          {/* Movements Table */}
          <div className="table-responsive rounded-4 shadow-sm border border-light-subtle bg-white">
            <Table hover size="sm" className="align-middle mb-0 text-nowrap" style={{ fontSize: "0.85rem" }}>
              <thead className="bg-primary text-white">
                <tr className="bg-primary text-white align-middle text-center">
                  <th className="py-2 bg-primary text-white" style={{ width: "95px" }}>Dată</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "130px" }}>Tip Document</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "100px" }}>Număr</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "70px" }}>Sens</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "100px" }}>Cantitate</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "100px" }}>Preț (RON)</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "110px" }}>Valoare (RON)</th>
                  <th className="py-2 bg-primary text-white">Partener / Explicație</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "150px" }}>Gestiune Sursă</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "150px" }}>Gestiune Dest.</th>
                  <th className="py-2 bg-primary text-white" style={{ width: "120px" }}>Stoc Cumulat</th>
                </tr>
              </thead>
              <tbody>
                {/* Initial Stock Balance Row */}
                <tr className="table-light text-muted fw-semibold">
                  <td className="text-center">—</td>
                  <td className="text-center">—</td>
                  <td className="text-center">—</td>
                  <td className="text-center">—</td>
                  <td className="text-end">—</td>
                  <td className="text-end">—</td>
                  <td className="text-end">—</td>
                  <td>STOC INIȚIAL DEȚINUT ÎN MAGAZIE</td>
                  <td className="text-center">—</td>
                  <td className="text-center">—</td>
                  <td className="text-end font-monospace">{fmtQty(data.initial_stock)}</td>
                </tr>

                {data.movements.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-4">
                      Nu există mișcări înregistrate în perioada selectată.
                    </td>
                  </tr>
                ) : (
                  data.movements.map((m, idx) => {
                    const isIn = m.direction === "IN";
                    const docLink = getDocLink(m.doc_type, m.doc_id);
                    const qtyVal = Number(m.qty || 0);
                    const priceVal = Number(m.price || 0);

                    return (
                      <tr key={idx}>
                        <td className="text-center">{formatDateRo(m.date)}</td>
                        <td className="text-center fw-semibold text-secondary">
                          {getDocTypeLabel(m.doc_type)}
                        </td>
                        <td className="text-center">
                          {docLink ? (
                            <Link to={docLink} className="fw-bold text-primary text-decoration-none d-print-none">
                              {m.doc_no || `#${m.doc_id}`}
                            </Link>
                          ) : (
                            <span className="fw-bold">{m.doc_no || `#${m.doc_id}`}</span>
                          )}
                          {/* Printable text version without link styling */}
                          <span className="d-none d-print-inline fw-bold">{m.doc_no || `#${m.doc_id}`}</span>
                        </td>
                        <td className="text-center">
                          {isIn ? (
                            <span className="badge bg-success-subtle text-success px-2 py-1">Intrare</span>
                          ) : (
                            <span className="badge bg-danger-subtle text-danger px-2 py-1">Ieșire</span>
                          )}
                        </td>
                        <td className="text-end font-monospace fw-semibold">
                          {fmtQty(m.qty)}
                        </td>
                        <td className="text-end font-monospace">
                          {fmtPrice(m.price)}
                        </td>
                        <td className="text-end font-monospace">
                          {fmtVal(qtyVal * priceVal)}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: "250px" }} title={m.contact_name || ""}>
                          {m.contact_name || "—"}
                        </td>
                        <td className="text-center text-truncate" style={{ maxWidth: "150px" }} title={m.gestiune_name || ""}>
                          {m.gestiune_name || "—"}
                        </td>
                        <td className="text-center text-truncate" style={{ maxWidth: "150px" }} title={m.gestiune2_name || ""}>
                          {m.gestiune2_name || "—"}
                        </td>
                        <td className="text-end font-monospace fw-bold">
                          {fmtQty(m.running_stock)}
                        </td>
                      </tr>
                    );
                  })
                )}

                {/* Final Stock Balance Row */}
                {data.movements.length > 0 && (
                  <tr className="table-secondary fw-bold">
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-end font-monospace text-success">{fmtQty(totals.inQty)}</td>
                    <td className="text-end font-monospace">—</td>
                    <td className="text-end font-monospace text-dark">{fmtVal(totals.inVal - totals.outVal)}</td>
                    <td>TOTAL MIȘCĂRI & SOLD FINAL</td>
                    <td className="text-center">—</td>
                    <td className="text-center">—</td>
                    <td className="text-end font-monospace text-primary">{fmtQty(data.final_stock)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
